import { useCallback } from 'react';
import { Formik, Form, Field } from 'formik';
import { useIntl } from 'react-intl';
import TextInput from 'components/inputs/TextInput';
import Button from 'components/common/Button';
import ScrollToError from 'components/common/ScrollToError';
import { Col, Row } from 'react-flexbox-grid';
import { ArrowBack, ArrowForward } from 'assets/icons';
import SaveIcon from 'assets/icons/save.svg?react';
import { Floor1, Floor2, Floor3 } from 'assets/icons/QuickCalculator';
import Img from 'assets/images/renovation-house/step-3.svg?react';
import SliderWithTooltip from 'components/inputs/SliderWithTooltip';
import ScrollToTop from 'components/common/ScrollToTop';
import ButtonSelect, { type IButtonSelectOption } from 'components/inputs/ButtonSelect';
import s from '../../calculators.module.scss';
import validationSchema from '../yupSchemas/renovationHouseThirdStepSchema';
import type { IRenovationHouseStepProps } from '../types';

const RenovationHouseThirdStep = ({
  onSubmitStep,
  lastStep,
  onSubmit,
  isEditMode,
  previousStep,
  nextStep,
  initialValues,
  formValuesRef,
  currentStep,
  onBack,
}: IRenovationHouseStepProps) => {
  const { formatMessage: t } = useIntl();

  const validateHouseAreaInput = useCallback(
    (houseArea: number) => {
      // Number(): `<` compares with null as 0 and with undefined as NaN, as before.
      if (isEditMode) {
        if (houseArea < Number(initialValues?.heated_living_area)) {
          return t(
            { id: 'renovation_house_wizard.wrong_min_house_area' },
            { value: initialValues.heated_living_area },
          );
        }
      } else if (houseArea < Number(formValuesRef?.current?.heated_living_area)) {
        return t(
          { id: 'renovation_house_wizard.wrong_min_house_area' },
          { value: formValuesRef.current.heated_living_area },
        );
      }
      return null;
    },
    [formValuesRef, initialValues, isEditMode, t],
  );

  return (
    <Row between="lg" center="md">
      <ScrollToTop />
      <Col md={8} lg={6}>
        <Formik
          initialValues={formValuesRef?.current}
          onSubmit={async (values, formikActions) => {
            onSubmitStep(values);
            await onSubmit({ values, formikActions, currentStep, isEditMode, lastStep, nextStep });
          }}
          validationSchema={validationSchema}
        >
          {({ isSubmitting, values }) => (
            <Form>
              <ScrollToError />
              <Field
                name="name"
                component={TextInput}
                type="text"
                label={t({ id: 'project_wizard.project_name' })}
              />
              <Field
                name="zip"
                component={TextInput}
                label={t({ id: 'project_wizard.zip_code' })}
              />
              <Field
                name="house_area"
                units="m²"
                component={SliderWithTooltip}
                validate={validateHouseAreaInput}
                step={1}
                min={initialValues?.heated_living_area}
                max={300}
                defaultValue={formValuesRef.current.heated_living_area}
                label={t({ id: 'renovation_house_wizard.house_area' })}
              />
              <Field
                name="number_of_floors"
                component={ButtonSelect}
                nullable={false}
                inOneRow={false}
                options={
                  [
                    {
                      topIcon: ({ altText }) => <Floor1 className="stroke" title={altText} />,
                      altText: t({ id: 'alt_text.new_building.number_of_floors.one_floor' }),
                      label: 1,
                      value: 1,
                    },
                    {
                      topIcon: ({ altText }) => <Floor2 className="stroke" title={altText} />,
                      altText: t({ id: 'alt_text.new_building.number_of_floors.two_floors' }),
                      label: 2,
                      value: 2,
                    },
                    {
                      topIcon: ({ altText }) => <Floor3 className="stroke" title={altText} />,
                      altText: t({ id: 'alt_text.new_building.number_of_floors.three_floors' }),
                      label: 3,
                      value: 3,
                    },
                  ] satisfies IButtonSelectOption[]
                }
                label={t({ id: `buying_house_wizard.floors` })}
              />

              <div className={s.buttonsWrapper}>
                {!isEditMode ? (
                  <Button
                    disabled={isSubmitting}
                    onClick={() => onBack(values, previousStep)}
                    startIcon={<ArrowBack />}
                  >
                    {t({ id: 'app.back' })}
                  </Button>
                ) : null}
                <Button
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  type="submit"
                  endIcon={isEditMode ? <SaveIcon /> : <ArrowForward />}
                  className="ml-auto"
                >
                  {t({ id: `app.${isEditMode ? 'save' : 'submit_form_next'}` })}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Col>
      <Col md={8} lg={6} first="xs" last="lg">
        <div className={s.imgBox}>
          <Img title={t({ id: 'alt_text.project_details_page.renovation_house.third_step' })} />
        </div>
      </Col>
    </Row>
  );
};

export default RenovationHouseThirdStep;
